import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import StopPayoutComponent from "./component";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";

const StopPayoutContainer = ({ getDataFromAPI }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [openSelect, setOpenSelect] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownSearch, setDropdownSearch] = useState(""); // New state for dropdown search

  const [formData, setFormData] = useState({
    franchiseeId: "",
    block: true
  });
  const [openDialog, setOpenDialog] = useState(false);

  // ✅ Fetch partners function
  const fetchPartners = useCallback(async (page = 0, isLoadMore = false) => {
    if ((isLoadMore && loadMoreLoading) || (!isLoadMore && loading) || !hasMore) return;
    
    if (isLoadMore) {
      setLoadMoreLoading(true);
    } else {
      setLoading(true);
    }
    
    try {
      const response = await new Promise((resolve, reject) => {
        getDataFromAPI(
          `/dashboard/api/v2/admin/franchisee?pageNumber=${page}&pageSize=${pageSize}`,
          "GET",
          undefined,
          resolve,
          reject
        );
      });

      if (page === 0) {
        setPartners(response);
        // Apply both search filters if exist
        if (dropdownSearch) {
          const filtered = filterPartners(response, dropdownSearch);
          setFilteredPartners(filtered);
        } else {
          setFilteredPartners(response);
        }
      } else {
        const newPartners = [...partners, ...response];
        setPartners(newPartners);
        // Apply both search filters if exist
        if (dropdownSearch) {
          const filtered = filterPartners(newPartners, dropdownSearch);
          setFilteredPartners(filtered);
        } else {
          setFilteredPartners(newPartners);
        }
      }

      setHasMore(response.length === pageSize);
      setPageNumber(page);
    } catch (err) {
      console.error("Partner API Error:", err);
      toast.error("Failed to load partners");
    } finally {
      if (isLoadMore) {
        setLoadMoreLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [loading, loadMoreLoading, hasMore, getDataFromAPI, pageSize, partners, dropdownSearch]);

  // ✅ Filter partners by search query
  const filterPartners = (partnersList, query) => {
    if (!query.trim()) return partnersList;
    
    const searchLower = query.toLowerCase().trim();
    
    return partnersList.filter(partner => {
      const partnerName = partner.name ? partner.name.toLowerCase() : "";
      const partnerId = partner.id ? partner.id.toString() : "";
      
      return partnerName.includes(searchLower) || 
             partnerId.includes(searchLower);
    });
  };

  // ✅ Handle dropdown search change
  const handleDropdownSearchChange = (e) => {
    const query = e.target.value;
    setDropdownSearch(query);
    
    if (!query.trim()) {
      setFilteredPartners(partners);
      return;
    }
    
    const filtered = filterPartners(partners, query);
    setFilteredPartners(filtered);
  };

  // ✅ Clear dropdown search
  const handleClearDropdownSearch = () => {
    setDropdownSearch("");
    setFilteredPartners(partners);
  };

  // ✅ Debounced search handler for main search (if you still want it)
  const handleSearchChange = useCallback(
    debounce((query) => {
      setSearchQuery(query);
      // You can keep this if you want a search outside the dropdown
    }, 300),
    []
  );

  // ✅ Clear main search
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleOpenSelect = (status) => {
    setOpenSelect(status);
    
    // When dropdown opens, reset dropdown search and fetch partners if not already loaded
    if (status) {
      setDropdownSearch("");
      if (partners.length === 0) {
        fetchPartners(0, false);
      } else {
        // Reset to show all partners when dropdown opens
        setFilteredPartners(partners);
      }
    }
  };

  // ✅ Load more button click handler
  const handleLoadMore = () => {
    if (!loadMoreLoading && hasMore) {
      fetchPartners(pageNumber + 1, true);
    }
  };

  // ✅ Scroll load more
  const handleScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
      !loadMoreLoading &&
      hasMore
    ) {
      handleLoadMore();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate selection
    if (!formData.franchiseeId) {
      toast.error("Please select a partner");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        franchiseeId: parseInt(formData.franchiseeId),
        block: true
      };

      await new Promise((resolve, reject) => {
        getDataFromAPI(
          "/partner/api/v3/acc/block-payout",
          "POST",
          payload,
          (response) => {
            setOpenDialog(true);
            resolve(response);
          },
          (error) => {
            toast.error(error.message || "Failed to block payout");
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while blocking payout");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    navigate(-1);
  };

  // Initial load on component mount
  useEffect(() => {
    fetchPartners(0, false);
  }, []);

  return (
    <StopPayoutComponent
      formData={formData}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
      loading={loading}
      partners={filteredPartners}
      openDialog={openDialog}
      handleCloseDialog={handleCloseDialog}
      navigate={navigate}
      handleOpenSelect={handleOpenSelect}
      openSelect={openSelect}
      handleScroll={handleScroll}
      hasMore={hasMore}
      handleLoadMore={handleLoadMore}
      loadMoreLoading={loadMoreLoading}
      dropdownSearch={dropdownSearch}
      handleDropdownSearchChange={handleDropdownSearchChange}
      handleClearDropdownSearch={handleClearDropdownSearch}
      allPartners={partners} // Pass all partners for stats
    />
  );
};

export default connect(null, { getDataFromAPI })(StopPayoutContainer);